// ============================================================================
// Beacon — "you own your data." A one-click, complete export of everything in
// your workspace as portable JSON (and deals as CSV). This is the concrete
// proof that Beacon's database is YOURS: your Supabase Postgres holds the
// canonical records, and you can walk away with all of them at any time. A CRM
// like GoHighLevel only ever gets a downstream copy — never the original.
// ============================================================================

import type { Deal } from "../types";
import { calc } from "../engine/engine";

export interface Backup {
  app: "beacon";
  version: 1;
  deals: Deal[];
  team: unknown[];
  buyBoxes: unknown[];
  scenarios: unknown[];
}

/** Assemble the full workspace backup (timestamp added by the caller). */
export function buildBackup(deals: Deal[], team: unknown[], buyBoxes: unknown[], scenarios: unknown[]): Backup {
  return { app: "beacon", version: 1, deals, team, buyBoxes, scenarios };
}

function csvCell(v: string | number): string {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Pure: a flat CSV of the deals with their headline computed metrics. */
export function dealsToCsv(deals: Deal[]): string {
  const head = ["name", "address", "market", "stage", "score", "band", "capRate", "dscr", "allIn", "stabValue", "rentCeiling2BR", "flood"];
  const rows = deals.map((d) => {
    const m = calc(d);
    return [
      d.name, d.addr, d.market, d.stage,
      m.score.gated ? "GATED" : m.score.composite, m.score.band,
      (m.capOnCost * 100).toFixed(1) + "%", m.dscr.toFixed(2) + "x",
      Math.round(m.allIn), Math.round(m.stabVal), d.safmr2 || "", d.flood,
    ].map(csvCell).join(",");
  });
  return [head.join(","), ...rows].join("\n");
}

/** DOM helper: trigger a browser download of text content. */
export function downloadText(filename: string, text: string, mime = "application/json") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
